package com.shadowings.gero.data.lessons.chapter16

import com.shadowings.gero.data.repository.AdjectivePairs
import com.shadowings.gero.data.repository.DatabaseRepositoryImpl
import com.shadowings.gero.domain.model.Characters
import com.shadowings.gero.domain.model.ExplanationContent
import com.shadowings.gero.domain.model.Faces
import com.shadowings.gero.domain.model.ICountableQuiz
import com.shadowings.gero.domain.model.IExplanation
import com.shadowings.gero.domain.model.IQuizContent
import com.shadowings.gero.domain.model.SingleSelectionContent
import com.shadowings.gero.domain.model.TrueFalseContent
import com.shadowings.gero.domain.model.WordSelectionContent
import com.shadowings.gero.data.repository.Hiero
import com.shadowings.gero.presentation.model.Word
import gero.composeapp.generated.resources.Res
import gero.composeapp.generated.resources.exp_content_ripassino_aggettivi_1
import gero.composeapp.generated.resources.exp_content_ripassino_aggettivi_2
import gero.composeapp.generated.resources.exp_content_ripassino_aggettivi_3
import gero.composeapp.generated.resources.exp_content_ripassino_aggettivi_4
import gero.composeapp.generated.resources.exp_content_ripassino_aggettivi_5
import gero.composeapp.generated.resources.exp_content_ripassino_aggettivi_6
import gero.composeapp.generated.resources.exp_title_ripassino_aggettivi
import kotlin.random.Random

fun getRipassinoAggettiviContent(): List<IExplanation> {
    return listOf(
        ExplanationContent(
            title = Res.string.exp_title_ripassino_aggettivi,
            content = Res.string.exp_content_ripassino_aggettivi_1,
            face = Faces.Happy,
            character = Characters.Masko,
        ),
        ExplanationContent(
            title = Res.string.exp_title_ripassino_aggettivi,
            content = Res.string.exp_content_ripassino_aggettivi_2,
            face = Faces.Doubt,
            character = Characters.Nilo,
        ),
        ExplanationContent(
            title = Res.string.exp_title_ripassino_aggettivi,
            content = Res.string.exp_content_ripassino_aggettivi_3,
            face = Faces.Serious,
            character = Characters.Zeta,
        ),
        ExplanationContent(
            title = Res.string.exp_title_ripassino_aggettivi,
            content = Res.string.exp_content_ripassino_aggettivi_4,
            face = Faces.Happy,
            character = Characters.Alyn,
        ),
        ExplanationContent(
            title = Res.string.exp_title_ripassino_aggettivi,
            content = Res.string.exp_content_ripassino_aggettivi_5,
            face = Faces.Surprised,
            character = Characters.Masko,
        ),
        ExplanationContent(
            title = Res.string.exp_title_ripassino_aggettivi,
            content = Res.string.exp_content_ripassino_aggettivi_6,
            face = Faces.Happy,
            character = Characters.Alyn,
        ),
    )
}

// A mix of the lessons before it: 1 sign, 2 vocabulary, 2 on gender and 2 on number (a
// translation and a pick-the-hieroglyphs each), 2 on the adjective used as a noun, and a single
// true/false drawn from all of them. The same phrase never comes out twice.
fun getRipassinoAggettiviQuizContent(
    hiero: List<Hiero>,
    words: List<Word>,
    languageCode: String,
    random: Random = Random,
    adjectivePairs: List<AdjectivePairs> = DatabaseRepositoryImpl.adjectivePairs
): List<IQuizContent> {
    val signs = getSegniDegliAggettiviQuizContent(hiero, languageCode)
    val vocabulary = getScortaDiAggettiviQuizContent(words, languageCode, random)
    val gender = getAggettiviGenereQuizContent(words, languageCode, random, adjectivePairs)
    val number = getAggettiviPluraleQuizContent(words, languageCode, random, adjectivePairs)
    val alone = getAggettivoSostantivatoQuizContent(words, languageCode, random, adjectivePairs)
    val lessons = listOf(signs, vocabulary, gender, number, alone)

    val picked = mutableListOf<IQuizContent>()
    val keys = mutableSetOf<String>()
    fun pick(candidates: List<IQuizContent>, count: Int) {
        candidates.shuffled(random)
            .filter { it !is TrueFalseContent }
            .filter { keys.add(questionKey(it)) }
            .take(count)
            .let(picked::addAll)
    }

    pick(signs, 1)
    pick(vocabulary, 2)
    listOf(gender, number).forEach { lesson ->
        pick(lesson.filterIsInstance<WordSelectionContent>(), 1)
        pick(lesson.filterIsInstance<SingleSelectionContent>(), 1)
    }
    pick(alone, 2)
    lessons.flatten().filterIsInstance<TrueFalseContent>().randomOrNull(random)?.let(picked::add)
    // Tops up from any lesson if one came out short.
    pick(lessons.flatten(), (10 - picked.size).coerceAtLeast(0))

    return picked.shuffled(random)
}

// What makes two questions the same one, across lessons.
private fun questionKey(quiz: IQuizContent): String = when (quiz) {
    is WordSelectionContent -> "ws:${quiz.hieroglyphs}"
    is SingleSelectionContent -> "ss:${quiz.detail}:${quiz.text}"
    else -> (quiz as? ICountableQuiz)?.id ?: quiz.toString()
}
